export * from "ce/selectors/organizationSelectors";
import { getAppsmithConfigs } from "ee/configs";
import {
  LICENSE_ORIGIN,
  LICENSE_PLAN,
  LICENSE_PLANS,
  LICENSE_TYPE,
  PRODUCT_EDITION,
} from "ee/pages/Billing/Types/types";

import type { AppState } from "ee/reducers";
import { getRemainingDaysFromTimestamp } from "ee/utils/billingUtils";
import { EE_PERMISSION_TYPE } from "ee/utils/permissionHelpers";
import { isNil } from "lodash";
import { createSelector } from "reselect";
import {} from "ce/selectors/organizationSelectors";

// Get cloudHosting from getAppsmithConfigs instead of importing it
const { cloudHosting } = getAppsmithConfigs();

export const isValidLicense = (state: AppState) => {
  const organizationConfig = state.organization?.organizationConfiguration;
  const isActivated = organizationConfig?.isActivated;

  /* checking if isActivated or license is null or undefined,
    to redirect it to /applications page instead of /license.
    This is because the user may already have a license but
    API response is incorrect and to retain the original flow as on CE.
  */
  if (isNil(isActivated) || isNil(organizationConfig?.license)) {
    return true;
  }

  if (isActivated) {
    if (organizationConfig?.license?.plan === LICENSE_PLANS.FREE) {
      return true;
    }

    if (
      organizationConfig?.license?.plan !== LICENSE_PLANS.FREE &&
      organizationConfig?.license?.active
    ) {
      return true;
    }
  }

  return false;
};

export const isLicenseExpired = (state: AppState) => {
  const organizationConfig = state.organization?.organizationConfiguration;
  const isActivated = organizationConfig?.isActivated;

  if (isActivated) {
    if (
      organizationConfig?.license &&
      organizationConfig?.license?.active === false &&
      organizationConfig?.license?.status === LICENSE_TYPE.EXPIRED
    ) {
      return true;
    }
  }

  return false;
};

export const getLicenseType = (state: AppState) => {
  return state.organization?.organizationConfiguration?.license?.type;
};

export const getLicenseExpiry = (state: AppState) => {
  const date = new Date(
    state.organization?.organizationConfiguration?.license?.expiry * 1000,
  ).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return date;
};

export const getLicenseKey = (state: AppState) => {
  return state.organization?.organizationConfiguration?.license?.key;
};

export const getLicenseId = (state: AppState) => {
  return state.organization?.organizationConfiguration?.license?.id;
};

export const getLicenseDetails = (state: AppState) => {
  return state.organization?.organizationConfiguration?.license;
};

export const getExpiry = (state: AppState) => {
  return state.organization?.organizationConfiguration?.license?.expiry;
};

export const getRemainingDays = createSelector(getExpiry, (expiry) => {
  const timeStamp = expiry * 1000;

  return getRemainingDaysFromTimestamp(timeStamp);
});

export const isTrialLicense = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.type ===
  LICENSE_TYPE.TRIAL;

export const isTrialExpiredLicense = (state: AppState) => {
  const { license } = state.organization?.organizationConfiguration;

  if (license) {
    return (
      license.type === LICENSE_TYPE.TRIAL &&
      license.status === LICENSE_TYPE.EXPIRED
    );
  }
};

export const isTrialActiveLicense = (state: AppState) => {
  const { license } = state.organization?.organizationConfiguration;

  if (license) {
    return (
      license.type === LICENSE_TYPE.TRIAL &&
      license.status !== LICENSE_TYPE.EXPIRED
    );
  }
};

export const isPaidExpiredLicense = (state: AppState) => {
  const { license } = state.organization?.organizationConfiguration;

  if (license) {
    return (
      license.type === LICENSE_TYPE.PAID &&
      license.status === LICENSE_TYPE.EXPIRED
    );
  }
};

export const isLicensePaymentFailed = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.status ===
  LICENSE_TYPE.PAYMENT_FAILED;

export const shouldShowLicenseBanner = (state: AppState) => {
  if (
    isTrialActiveLicense(state) ||
    (isAdminUser(state) &&
      (isTrialExpiredLicense(state) ||
        isPaidExpiredLicense(state) ||
        isLicensePaymentFailed(state)))
  ) {
    return true;
  }

  return false;
};

export const hasInvalidLicenseKeyError = (state: AppState) => {
  return state.organization.organizationConfiguration?.license
    ?.invalidLicenseKeyError;
};

export const isAdminUser = (state: AppState) =>
  state.organization?.userPermissions?.includes(
    EE_PERMISSION_TYPE.MANAGE_ORGANIZATION,
  );

export const isLicenseValidating = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.validatingLicense;

export const isLicenseUpdatingFree = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.isFree;

export const isRemovingLicense = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.removingLicense;

export const isLicenseRefreshing = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.refreshingLicense;

export const getLicensePlan = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.plan;

export const getProductEdition = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.productEdition;

export const isLicenseModalOpen = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.showLicenseModal;

export const isRemoveLicenseModalOpen = (state: AppState) =>
  state.organization?.organizationConfiguration?.license
    ?.showRemoveLicenseModal;

export const isDowngradeLicenseModalOpen = (state: AppState) =>
  state.organization?.organizationConfiguration?.license
    ?.showDowngradeLicenseModal;

export const isEnterprise = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.plan ===
    LICENSE_PLAN.ENTERPRISE &&
  state.organization?.organizationConfiguration?.license?.productEdition ===
    PRODUCT_EDITION.COMMERCIAL;

export const isBusiness = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.plan ===
    LICENSE_PLAN.BUSINESS &&
  state.organization?.organizationConfiguration?.license?.productEdition ===
    PRODUCT_EDITION.COMMERCIAL;

export const isTeam = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.plan ===
    LICENSE_PLAN.BUSINESS_AI &&
  state.organization?.organizationConfiguration?.license?.productEdition ===
    PRODUCT_EDITION.COMMERCIAL;

export const isBusinessCloud = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.origin ===
  LICENSE_ORIGIN.APPSMITH_CLOUD;

export const isAirgapLicense = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.plan ===
    LICENSE_PLAN.ENTERPRISE &&
  state.organization?.organizationConfiguration?.license?.productEdition ===
    PRODUCT_EDITION.AIR_GAP;

export const getStartDate = (state: AppState) => {
  const date =
    state.organization?.organizationConfiguration?.license?.subscriptionDetails
      ?.startDate;

  if (!date) return date;

  const parsedDate = new Date(date * 1000).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return parsedDate;
};

export const getEndDate = (state: AppState) => {
  const date =
    state.organization?.organizationConfiguration?.license?.subscriptionDetails
      ?.startDate;

  if (!date) return date;

  const parsedDate = new Date(
    state.organization?.organizationConfiguration?.license?.subscriptionDetails
      ?.endDate * 1000,
  ).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return parsedDate;
};

export const getCustomerEmail = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.subscriptionDetails
    ?.customerEmail;

/**
 * selects the organization brand colors
 *
 * @returns
 */
export const getBrandColors = (state: AppState) => {
  if (!cloudHosting) {
    return state.organization?.organizationConfiguration?.brandColors || {};
  }

  return {};
};

export const isFreePlan = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.plan ===
  LICENSE_PLANS.FREE;

export const isOrganizationActivated = (state: AppState) =>
  state.organization?.organizationConfiguration?.isActivated;

export const isWithinAnOrganization = (state: AppState) =>
  state.organization.isWithinAnOrganization;

export const hasLicenseSubscriptionError = (state: AppState) => {
  const licenseError =
    state.organization?.organizationConfiguration?.license?.licenseError;

  return licenseError && licenseError.length > 0;
};

export const getTenantId = (state: AppState) => state.organization?.tenantId;

export const isCheckingOut = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.isCheckingOut;

export const isTrialLoading = (state: AppState) =>
  state.organization?.organizationConfiguration?.license?.isTrialLoading;
